"use client";

import React from "react";
import { Activity, Brain, MessageCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { Card, CardContent } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const services = [{
  id: "pt",
  title: "專業醫護",
  subtitle: "Professional Care",
  icon: Activity,
  headerClass: "bg-primary text-primary-foreground",
  summary: "由資深護士及保健員提供專業到戶護理，涵蓋傷口管理、藥物指導及健康監測。",
  details: "我們的護理團隊具備豐富臨床經驗，為長者及術後病人提供全方位的醫療支援。服務包括：造口護理、導尿管更換、注射服務、生命徵象監測及慢性病管理。我們強調「芯」繫健康的理念，確保每位使用者都能在熟悉的環境中獲得專業照護。適合手術後復康、長期病患或需要專業醫療監察的人士。"
}, {
  id: "ot",
  title: "復康治療",
  subtitle: "Rehabilitation",
  icon: Brain,
  headerClass: "bg-secondary text-secondary-foreground",
  summary: "結合物理、職業及言語治療，針對中風、骨科手術或認知障礙提供個人化方案。",
  details: "透過專業評估，我們為服務使用者制定「賦能」計劃。物理治療專注於肌力恢復與平衡訓練；職業治療則著重於日常生活技能重建及家居環境安全改裝。言語治療亦提供吞嚥及溝通訓練，協助患者重拾生活質素，達到可瞻性的預後。適合中風康復者、骨科術後患者或患有認知障礙症的人士。"
}, {
  id: "st",
  title: "生活支援",
  subtitle: "Holistic Support",
  icon: MessageCircle,
  headerClass: "bg-accent text-accent-foreground border-b border-border/20",
  summary: "提供陪診、日常生活照顧及到戶膳食，全方位減輕照顧者壓力並提升生活尊嚴。",
  details: "我們了解照顧者的辛勞，提供多元化的支援服務。專業陪診員協助就醫，生活助理負責個人衛生及家居清潔。此外，我們亦提供營養師設計的到戶膳食服務。透過社區資源聯繫，我們與家庭攜手，共同打造一個充滿溫度與尊嚴的居家環境。適合獨居長者、行動不便人士及需要喘息空間的家庭照顧者。"
}];
export default function AlliedHealthServices() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="74323" id="多元化護理體系" className="bg-background text-foreground py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div ref={headerRef} className={cn("mx-auto mb-16 max-w-3xl text-center transition-all duration-700 ease-out", isHeaderVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0")}>
          <h2 className="text-foreground mb-4 text-3xl font-bold tracking-tight sm:text-4xl">
            多元化護理體系 專業居家支援
          </h2>
          <p className="text-muted-foreground text-lg leading-relaxed">
            憬芯醫療 (Prospect Care) 秉持「憬於未來，芯繫健康」的理念，由資深醫護團隊為您提供全方位、個人化的居家照護方案，讓每一位服務使用者在熟悉的環境中重拾健康與尊嚴。
          </p>
        </div>

        <div ref={gridRef} className={cn("grid grid-cols-1 gap-8 md:grid-cols-3 transition-all duration-1000 delay-200 ease-out", isGridVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0")}>
          {services.map((service, i) => {
          const Icon = service.icon;
          return <Card key={service.id} data-index={i} className="flex h-full flex-col gap-0 overflow-hidden p-0 shadow-md transition-all duration-300 hover:shadow-xl rounded-3xl border-border/50">
                <div className={cn("flex items-center gap-4 p-6", service.headerClass)}>
                  <div className="bg-white/20 rounded-full p-3 backdrop-blur-sm">
                    <Icon className="size-6" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold tracking-tight">{service.title}</h3>
                    <p className="text-sm opacity-90 font-medium">{service.subtitle}</p>
                  </div>
                </div>

                <CardContent className="flex flex-1 flex-col gap-4 p-6">
                  <p className="text-muted-foreground text-base leading-relaxed">
                    {service.summary}
                  </p>

                  <div className="mt-auto pt-4">
                    <Accordion type="single" collapsible className="w-full">
                      <AccordionItem value="details" className="border-none">
                        <AccordionTrigger className="text-primary hover:text-primary/80 py-2 text-sm font-bold hover:no-underline transition-colors">
                          了解更多詳情
                        </AccordionTrigger>
                        <AccordionContent className="text-muted-foreground pt-2 text-sm leading-relaxed">
                          {service.details}
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  </div>
                </CardContent>
              </Card>;
        })}
        </div>
      </div>
    </section>;
}